﻿/* ═══════════════════════════════════════════════════════════════════
   NUVORA PDP — JavaScript
   Gallery · Variants · Sticky Bar · Scroll Reveal · Metrics Counter · AJAX Cart
   ═══════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ── Utility: format money from cents ── */
  function formatMoney(cents) {
    const pounds = cents / 100;
    return '£' + (pounds % 1 === 0 ? pounds.toFixed(0) : pounds.toFixed(2));
  }

  /* ════════════════════════════════════════════════
     GALLERY
     ════════════════════════════════════════════════ */
  const ZenVoraGallery = {
    activeIndex: 0,
    _autoplayTimer: null,

    init() {
      const thumbs = document.querySelectorAll('.zenvora-gallery__thumb');
      thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          this.stopAutoplay();
          this.goTo(parseInt(thumb.dataset.index, 10));
        });
      });

      /* Prev / Next buttons */
      const prevBtn = document.getElementById('zenvora-gallery-prev');
      const nextBtn = document.getElementById('zenvora-gallery-next');
      if (prevBtn) prevBtn.addEventListener('click', () => { this.stopAutoplay(); this.prev(); });
      if (nextBtn) nextBtn.addEventListener('click', () => { this.stopAutoplay(); this.next(); });

      /* Swipe support */
      const main = document.getElementById('zenvora-gallery-main');
      if (!main) return;

      let startX = 0;
      let startY = 0;

      main.addEventListener('touchstart', e => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true });

      main.addEventListener('touchend', e => {
        const dx = startX - e.changedTouches[0].clientX;
        const dy = startY - e.changedTouches[0].clientY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
          this.stopAutoplay();
          dx > 0 ? this.next() : this.prev();
        }
      }, { passive: true });

      /* Keyboard navigation when gallery is focused */
      const gallery = document.getElementById('zenvora-gallery');
      if (gallery) {
        gallery.setAttribute('tabindex', '0');
        gallery.addEventListener('keydown', e => {
          if (e.key === 'ArrowRight') { this.stopAutoplay(); this.next(); }
          if (e.key === 'ArrowLeft')  { this.stopAutoplay(); this.prev(); }
        });
      }

      /* Start on second image on PDP open (first image stays as card/collection thumbnail) */
      const allSlides = document.querySelectorAll('.zenvora-gallery__slide');
      if (allSlides.length > 1) { this.goTo(1); }
    },

    startAutoplay() {
      const slides = document.querySelectorAll('.zenvora-gallery__slide');
      if (slides.length < 2) return;
      this._autoplayTimer = setInterval(() => {
        this.goTo(this.activeIndex === 0 ? 1 : 0, false);
      }, 3000);
    },

    stopAutoplay() {
      if (this._autoplayTimer) {
        clearInterval(this._autoplayTimer);
        this._autoplayTimer = null;
      }
    },

    goTo(index, scrollThumb = true) {
      const slides = document.querySelectorAll('.zenvora-gallery__slide');
      const thumbs = document.querySelectorAll('.zenvora-gallery__thumb');

      slides.forEach((slide, i) => {
        slide.classList.toggle('zenvora-gallery__slide--hidden', i !== index);
      });

      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('active', i === index);
        thumb.setAttribute('aria-pressed', String(i === index));
      });

      /* Scroll active thumb into view — skipped during autoplay to prevent page jumping */
      if (scrollThumb) {
        const activeThumb = document.querySelector('.zenvora-gallery__thumb.active');
        if (activeThumb) {
          activeThumb.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
        }
      }

      this.activeIndex = index;
    },

    goToByMediaId(mediaId) {
      const data = window.zenvoraProductData;
      if (!data) return;
      const idx = data.media.findIndex(m => m.id === mediaId);
      if (idx >= 0) this.goTo(idx);
    },

    next() {
      const count = document.querySelectorAll('.zenvora-gallery__slide').length;
      this.goTo((this.activeIndex + 1) % count);
    },

    prev() {
      const count = document.querySelectorAll('.zenvora-gallery__slide').length;
      this.goTo((this.activeIndex - 1 + count) % count);
    }
  };

  /* ════════════════════════════════════════════════
     VARIANT SELECTOR
     ════════════════════════════════════════════════ */
  const ZenVoraVariants = {
    selectedOptions: [],

    init() {
      const data = window.zenvoraProductData;
      if (!data) return;

      /* Build initial selected options from active swatches */
      const activeSwatches = document.querySelectorAll('.zenvora-swatch.active');
      this.selectedOptions = Array.from(activeSwatches).map(s => s.dataset.value);

      document.querySelectorAll('.zenvora-swatch').forEach(swatch => {
        swatch.addEventListener('click', () => this.onSwatchClick(swatch));
      });
    },

    onSwatchClick(swatch) {
      const optionIndex = parseInt(swatch.dataset.optionIndex, 10);
      const value = swatch.dataset.value;

      /* Update active state within this option group */
      const group = swatch.closest('.zenvora-option__swatches');
      group.querySelectorAll('.zenvora-swatch').forEach(s => {
        s.classList.remove('active');
        s.setAttribute('aria-pressed', 'false');
      });
      swatch.classList.add('active');
      swatch.setAttribute('aria-pressed', 'true');

      /* Update label */
      const label = document.getElementById('zenvora-option-value-' + optionIndex);
      if (label) label.textContent = value;

      /* Update selected options array */
      this.selectedOptions[optionIndex] = value;

      /* Find matching variant */
      const variant = this.findVariant();
      if (variant) this.applyVariant(variant);
    },

    findVariant() {
      const { variants } = window.zenvoraProductData;
      return variants.find(v =>
        v.options.every((opt, i) => opt === this.selectedOptions[i])
      ) || null;
    },

    applyVariant(variant) {
      /* Update hidden form inputs */
      document.querySelectorAll('#zenvora-variant-id, .zenvora-express-variant-id').forEach(input => {
        input.value = variant.id;
      });

      /* Update URL without reload */
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({ variantId: variant.id }, '', url.toString());

      /* Update price */
      this.updatePrice(variant);

      /* Update button */
      this.updateButton(variant);

      /* Update gallery image */
      if (variant.featured_media) {
        ZenVoraGallery.goToByMediaId(variant.featured_media.id);
      }

      /* Update Klarna widget */
      this.updateKlarna(variant.price);

      /* Update sticky bar price */
      this.updateStickyPrice(variant);
    },

    updatePrice(variant) {
      const container = document.getElementById('zenvora-price');
      if (!container) return;

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const saving = variant.compare_at_price - variant.price;
        const savingPct = Math.round((saving / variant.compare_at_price) * 100);
        container.innerHTML =
          '<span class="zenvora-price__sale">' + formatMoney(variant.price) + '</span>' +
          '<s class="zenvora-price__original">' + formatMoney(variant.compare_at_price) + '</s>' +
          '<span class="zenvora-price__save-badge">Save ' + savingPct + '%</span>';
      } else {
        container.innerHTML =
          '<span class="zenvora-price__regular">' + formatMoney(variant.price) + '</span>';
      }
    },

    updateButton(variant) {
      const btn     = document.getElementById('zenvora-atc-btn');
      const btnText = btn && btn.querySelector('.zenvora-btn-atc__text');
      if (!btn || !btnText) return;

      if (variant.available) {
        btn.disabled = false;
        const ctaText = btn.dataset.cta || 'Add to Cart';
        btnText.textContent = ctaText + ' — ' + formatMoney(variant.price);
      } else {
        btn.disabled = true;
        btnText.textContent = 'Sold out';
      }
    },

    updateKlarna(price) {
      const placement = document.querySelector('klarna-placement');
      if (!placement) return;
      placement.setAttribute('data-purchase-amount', price);
      if (window.Klarna && window.Klarna.OnsiteMessaging) {
        window.Klarna.OnsiteMessaging.refresh();
      }
    },

    updateStickyPrice(variant) {
      const el = document.getElementById('zenvora-sticky-price');
      if (!el) return;
      let html = formatMoney(variant.price);
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        html += '&nbsp;<s>' + formatMoney(variant.compare_at_price) + '</s>';
      }
      el.innerHTML = html;
    }
  };

  /* ════════════════════════════════════════════════
     STICKY BAR — mobile
     ════════════════════════════════════════════════ */
  const NuvStickyBar = {
    bar: null,
    atcBtn: null,

    init() {
      this.bar    = document.getElementById('zenvora-sticky-bar');
      this.atcBtn = document.getElementById('zenvora-atc-btn');
      if (!this.bar || !this.atcBtn) return;

      let hasScrolled = false;
      window.addEventListener('scroll', () => { hasScrolled = true; }, { once: true, passive: true });

      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            /* Only show sticky bar after user has scrolled — prevents firing on page load when ATC is below fold */
            const show = hasScrolled && !entry.isIntersecting;
            this.bar.classList.toggle('is-visible', show);
            this.bar.setAttribute('aria-hidden', String(!show));
          });
        },
        { threshold: 0, rootMargin: '0px' }
      );

      observer.observe(this.atcBtn);

      /* Sticky bar CTA clicks trigger main form submit */
      const stickyCta = document.getElementById('zenvora-sticky-cta');
      if (stickyCta) {
        stickyCta.addEventListener('click', () => {
          const form = document.getElementById('zenvora-product-form');
          if (form) form.requestSubmit();
        });
      }
    }
  };

  /* ════════════════════════════════════════════════
     AJAX CART
     ════════════════════════════════════════════════ */
  const NuvCart = {
    init() {
      const form = document.getElementById('zenvora-product-form');
      if (!form) return;

      form.addEventListener('submit', async e => {
        e.preventDefault();

        const btn = document.getElementById('zenvora-atc-btn');
        if (btn) btn.classList.add('zenvora-btn-atc--loading');

        const variantId = document.getElementById('zenvora-variant-id').value;

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
          });

          if (!res.ok) throw new Error('Cart add failed');

          /* Trigger theme cart drawer / bubble update */
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));

          /* Update cart count bubble if present */
          const countRes = await fetch('/cart.js');
          const cartData = await countRes.json();
          document.querySelectorAll('[data-cart-count]').forEach(el => {
            el.textContent = cartData.item_count;
          });

          /* Open theme cart if available, else redirect */
          if (typeof theme !== 'undefined' && theme.cart && theme.cart.open) {
            theme.cart.open();
          } else {
            window.location.href = '/cart';
          }
        } catch (err) {
          console.error('[ZenVora] Cart add error:', err);
        } finally {
          if (btn) btn.classList.remove('zenvora-btn-atc--loading');
        }
      });
    }
  };

  /* ════════════════════════════════════════════════
     SCROLL REVEAL (AOS-like, no dependency)
     ════════════════════════════════════════════════ */
  const NuvReveal = {
    init() {
      const els = document.querySelectorAll('.zenvora-reveal');
      if (!els.length) return;

      /* Opt in: mark body so CSS hides elements — only when JS is running */
      document.body.classList.add('zenvora-js');

      const reveal = el => el.classList.add('is-revealed');

      /* Reveal only elements currently visible in the viewport */
      const revealVisible = () => {
        document.querySelectorAll('.zenvora-reveal:not(.is-revealed)').forEach(el => {
          const rect = el.getBoundingClientRect();
          if (rect.top < window.innerHeight - 20 && rect.bottom > 0) reveal(el);
        });
      };

      /* Immediately reveal elements already on screen */
      revealVisible();

      /* IntersectionObserver: fires when element is 20px inside viewport */
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              reveal(entry.target);
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0, rootMargin: '0px 0px -20px 0px' }
      );

      document.querySelectorAll('.zenvora-reveal:not(.is-revealed)').forEach(el => {
        observer.observe(el);
      });

      /* Safety net: every 1.5 s reveal only what is VISIBLE — never reveals off-screen elements */
      const safety = setInterval(() => {
        const hidden = document.querySelectorAll('.zenvora-reveal:not(.is-revealed)');
        if (!hidden.length) { clearInterval(safety); return; }
        revealVisible();
      }, 1500);
    }
  };

  /* ════════════════════════════════════════════════
     METRICS COUNTER (requestAnimationFrame)
     ════════════════════════════════════════════════ */
  const NuvCounter = {
    duration: 1600,

    init() {
      const counters = document.querySelectorAll('.zenvora-counter');
      if (!counters.length) return;

      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.animateCounter(entry.target);
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.5 }
      );

      counters.forEach(counter => observer.observe(counter));
    },

    animateCounter(el) {
      const target   = parseFloat(el.dataset.target);
      const decimals = parseInt(el.dataset.decimals || '0', 10);
      const prefix   = el.dataset.target.trim().startsWith('+') ? '+' : '';
      const start    = performance.now();

      const step = (now) => {
        const elapsed  = now - start;
        const progress = Math.min(elapsed / this.duration, 1);
        const eased    = 1 - Math.pow(1 - progress, 3); /* ease-out cubic */
        const value    = target * eased;

        el.textContent = prefix + (decimals > 0
          ? value.toFixed(decimals)
          : Math.floor(value).toString());

        if (progress < 1) requestAnimationFrame(step);
        else el.textContent = prefix + (decimals > 0 ? target.toFixed(decimals) : Math.floor(target).toString());
      };

      requestAnimationFrame(step);
    }
  };

  /* ════════════════════════════════════════════════
     STAR ANIMATION (sequential entrance on scroll)
     ════════════════════════════════════════════════ */
  const NuvStars = {
    init() {
      const containers = document.querySelectorAll('.zenvora-stars-animate');
      if (!containers.length) return;

      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.animate(entry.target);
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0, rootMargin: '0px 0px -60px 0px' }
      );

      containers.forEach(c => observer.observe(c));
    },

    animate(container) {
      const speed = parseInt(container.dataset.speed, 10) || 150;
      container.querySelectorAll('.zenvora-star-anim').forEach((star, i) => {
        setTimeout(() => star.classList.add('is-visible'), i * speed);
      });
    }
  };

  /* ════════════════════════════════════════════════
     INIT on DOMContentLoaded
     ════════════════════════════════════════════════ */
  function init() {
    ZenVoraGallery.init();
    ZenVoraVariants.init();
    NuvStickyBar.init();
    NuvCart.init();
    NuvReveal.init();
    NuvCounter.init();
    NuvStars.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
